/**
 * Melqarto — le géocodage et les itinéraires de Dingo, auto-hébergés pour la
 * Tunisie. Les mêmes services que l'app mobile (PlacesService, RoutingService
 * côté KMP), parlés depuis le navigateur :
 *
 * - `places.dingo.tn` : Nominatim. Adresse d'un point (reverse), point d'une
 *   adresse (search).
 * - `routing.dingo.tn` : Valhalla. Tracé et longueur d'un itinéraire selon le
 *   mode de déplacement.
 *
 * Le back-office passait par BigDataCloud puis le Nominatim public, avec une
 * seconde d'attente entre deux appels pour ne pas se faire refuser : la
 * Flotte mettait une minute à nommer ses villes. Ici, pas de quota étranger,
 * pas de délai.
 */

const PLACES_BASE_URL = (process.env.REACT_APP_PLACES_URL || 'https://places.dingo.tn').replace(/\/$/, '');
const ROUTING_BASE_URL = (process.env.REACT_APP_ROUTING_URL || 'https://routing.dingo.tn').replace(/\/$/, '');

export interface LatLng {
  lat: number;
  lng: number;
}

export interface GeocodedAddress {
  /** Adresse complète, telle que Nominatim la compose. */
  displayName: string;
  /** Localité, tous niveaux confondus — voir `cityOf`. */
  city: string | null;
  postcode: string | null;
}

export interface PlaceSuggestion {
  displayName: string;
  position: LatLng;
}

/**
 * Localité d'une réponse Nominatim. Toutes n'ont pas un tag `city` dans OSM,
 * et ça varie même entre gouvernorats tunisiens : Tunis est taguée `city`,
 * mais Ariana (et d'autres capitales de gouvernorat) ressort en `county`.
 * On essaie tous les niveaux plausibles avant d'abandonner — même ordre que
 * l'app, pour que la Flotte et l'annonce disent la même ville.
 */
function cityOf(address: any): string | null {
  if (!address || typeof address !== 'object') return null;
  return address.city || address.town || address.village || address.municipality || address.county || null;
}

const memory = new Map<string, Promise<GeocodedAddress | null>>();

/** Quatre décimales ≈ 11 m : deux annonces au même coin de rue partagent une réponse. */
const reverseKey = (lat: number, lng: number) => `${lat.toFixed(4)},${lng.toFixed(4)}`;

/**
 * Adresse d'un point. `null` quand Melqarto ne connaît rien à cet endroit
 * (pleine mer, désert) ou ne répond pas — l'appelant décide quoi afficher,
 * on ne fabrique pas de « Ville inconnue ».
 *
 * Mémorisée pour la durée de la page : la Flotte redemande les mêmes points
 * à chaque retour sur l'écran.
 */
export function reverseGeocode(lat: number, lng: number): Promise<GeocodedAddress | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return Promise.resolve(null);
  const key = reverseKey(lat, lng);
  const cached = memory.get(key);
  if (cached) return cached;

  const request = (async () => {
    try {
      const url = `${PLACES_BASE_URL}/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=fr,ar,en&addressdetails=1`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const json = await response.json();
      if (!json || json.error || typeof json.display_name !== 'string') return null;
      return {
        displayName: json.display_name,
        city: cityOf(json.address),
        postcode: json.address?.postcode ?? null
      };
    } catch {
      return null;
    }
  })();

  memory.set(key, request);
  // Un échec ne doit pas rester en mémoire : le réseau revient, la réponse aussi.
  request.then((result) => { if (result === null) memory.delete(key); });
  return request;
}

/**
 * Points correspondant à une adresse ou un nom de lieu. Limité à la Tunisie ;
 * `near` favorise les résultats autour d'un point (le centre de la carte)
 * sans exclure les autres — « Avenue Habib Bourguiba » existe dans chaque
 * ville du pays, celle qu'on cherche est celle qu'on regarde.
 */
export async function searchPlaces(query: string, near?: LatLng, signal?: AbortSignal): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const params = new URLSearchParams({
    q,
    format: 'json',
    countrycodes: 'tn',
    'accept-language': 'fr,ar,en',
    limit: '6'
  });
  if (near) {
    const d = 0.35; // ≈ 35 km de côté : l'agglomération, pas la rue
    params.set('viewbox', `${near.lng - d},${near.lat + d},${near.lng + d},${near.lat - d}`);
    params.set('bounded', '0');
  }
  const response = await fetch(`${PLACES_BASE_URL}/search?${params.toString()}`, {
    headers: { Accept: 'application/json' },
    signal
  });
  if (!response.ok) throw new Error(`Recherche d'adresse : ${response.status}`);
  const json = await response.json();
  if (!Array.isArray(json)) return [];
  return json
    .map((item: any) => ({
      displayName: String(item.display_name || ''),
      position: { lat: Number(item.lat), lng: Number(item.lon) }
    }))
    .filter((s: PlaceSuggestion) => s.displayName && Number.isFinite(s.position.lat) && Number.isFinite(s.position.lng));
}

/** Modes de déplacement Valhalla employés par Dingo. */
export type RoutingCosting = 'auto' | 'bicycle' | 'motorcycle' | 'motor_scooter' | 'pedestrian';

export const COSTING_LABELS: Record<RoutingCosting, string> = {
  auto: 'en voiture',
  bicycle: 'à vélo',
  motorcycle: 'à moto',
  motor_scooter: 'en scooter',
  pedestrian: 'à pied'
};

/**
 * Un vélo n'emprunte pas les mêmes rues qu'une voiture : router tout en
 * `auto` donnerait un tracé et une longueur faux. Même table que l'app.
 * `null` pour ce qui ne circule pas sur le réseau routier : demander un
 * itinéraire à un jetski renverrait soit une erreur, soit un trajet par la
 * route entre deux ports, ce qui serait faux.
 */
export function routingCostingForVehicleType(vehicleType: string | null | undefined): RoutingCosting | null {
  switch ((vehicleType || '').toLowerCase()) {
    case 'car':
    case 'van':
    case 'quad':
      return 'auto';
    case 'motorcycle':
      return 'motorcycle';
    case 'scooter':
    case 'trottinette':
    case 'kickscooter':
      return 'motor_scooter';
    case 'bike':
      return 'bicycle';
    default:
      return null;
  }
}

export interface RouteDetails {
  /** Tracé, en [lat, lng] — le format des Polyline de Leaflet. */
  points: [number, number][];
  /**
   * Longueur DE L'ITINÉRAIRE entre les deux points, en km — pas la distance
   * réellement parcourue par le locataire, l'app ne relève aucun compteur.
   * Les écrans qui l'affichent doivent le dire ainsi.
   */
  distanceKm: number | null;
}

/**
 * Itinéraire entre deux points.
 *
 * En GET avec le JSON dans l'URL, et non en POST : Valhalla répond aux
 * requêtes du navigateur mais pas au préflight OPTIONS qu'un POST JSON
 * déclenche — le POST est refusé par la politique CORS avant même de partir.
 * Le GET est une « requête simple » qui n'en a pas besoin.
 */
export async function getRoute(from: LatLng, to: LatLng, costing: RoutingCosting, signal?: AbortSignal): Promise<RouteDetails> {
  const body = {
    locations: [
      { lat: from.lat, lon: from.lng },
      { lat: to.lat, lon: to.lng }
    ],
    costing,
    units: 'kilometers'
  };
  const response = await fetch(`${ROUTING_BASE_URL}/route?json=${encodeURIComponent(JSON.stringify(body))}`, {
    headers: { Accept: 'application/json' },
    signal
  });
  if (!response.ok) throw new Error(`Itinéraire : ${response.status}`);
  const json = await response.json();
  const legs: any[] = json?.trip?.legs;
  if (!Array.isArray(legs)) throw new Error('Réponse de routage invalide');

  const points = legs.flatMap((leg) => (typeof leg?.shape === 'string' ? decodePolyline6(leg.shape) : []));
  if (points.length === 0) throw new Error('Aucun itinéraire trouvé');

  // `units: kilometers` dans la requête : la longueur revient donc en km.
  const length = Number(json.trip?.summary?.length);
  return { points, distanceKm: Number.isFinite(length) ? length : null };
}

/**
 * Décode le format « polyline6 » de Valhalla (précision 1e6, contrairement au
 * polyline5 de Google Maps). Voir la doc Valhalla : `trip.legs[].shape`.
 */
export function decodePolyline6(encoded: string): [number, number][] {
  const precision = 1_000_000;
  const points: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  const readDelta = () => {
    let shift = 0;
    let result = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };

  while (index < encoded.length) {
    lat += readDelta();
    lng += readDelta();
    points.push([lat / precision, lng / precision]);
  }
  return points;
}

/** Distance à vol d'oiseau, en km. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Deux points séparés de moins de ce seuil sont le même lieu de rendez-vous :
 * un itinéraire de 30 m n'apprend rien et s'affiche comme une tache.
 */
export const SAME_PLACE_THRESHOLD_KM = 0.08;

export const formatKm = (km: number) =>
  km < 1 ? `${Math.round(km * 1000)} m` : `${km.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km`;

/** Convertit ce que l'API renvoie (nombre, chaîne, null) en position, ou rien. */
export function positionOrNull(lat: unknown, lng: unknown): LatLng | null {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln) || (la === 0 && ln === 0)) return null;
  return { lat: la, lng: ln };
}
